import React from 'react';
import ReactDOM from 'react-dom';
import { AutoSizer } from 'react-virtualized/dist/commonjs/AutoSizer';
import { List } from 'react-virtualized/dist/commonjs/List';
import { CellMeasurer, CellMeasurerCache } from 'react-virtualized/dist/commonjs/CellMeasurer';
import { isEqual } from 'lodash';
import { Wrapper } from './helpers/Wrapper';
import { CategorizedContentProps, CategorizedContentState, ItemData } from './typesCategorizedContent';

/* Components */
export class CategorizedContent extends React.PureComponent<CategorizedContentProps, CategorizedContentState> {
  cellMeasurerCache = new CellMeasurerCache({
    fixedWidth: true,
  });

  data: ItemData[] = [];

  listRef = React.createRef<any>();

  constructor(props) {
    super(props);
    this.state = { listWidth: props.initialListWidth, windowHeight: undefined, windowWidth: undefined };
  }

  componentDidMount(): void {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps: Readonly<CategorizedContentProps>, prevState: Readonly<CategorizedContentState>): void {
    const { content, forceHideHeaders, headers, initialListWidth, items } = this.props;
    const { windowHeight, windowWidth } = this.state;

    /* eslint-disable-next-line react/no-find-dom-node */
    const listDOMNode = ReactDOM.findDOMNode(this.listRef.current);
    const newListWidth = listDOMNode.parentElement.getBoundingClientRect().width || initialListWidth;

    if (
      !isEqual(
        [
          prevProps.content,
          prevProps.forceHideHeaders,
          prevProps.headers,
          prevProps.items,
          prevState.listWidth,
          prevState.windowHeight,
          prevState.windowWidth,
        ],
        [content, forceHideHeaders, headers, items, newListWidth, windowHeight, windowWidth],
      )
    ) {
      this.cellMeasurerCache.clearAll();
      /* eslint-disable-next-line react/no-did-update-set-state */
      this.setState({ listWidth: newListWidth });
      this.listRef.current && this.listRef.current.forceUpdateGrid();
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener('resize', this.handleResize);
  }

  areAllAndNonzeroNodesHidden = (): boolean => {
    const { content } = this.props;
    return this.data.length === 0 && content.length > 0;
  };

  calculateListHeight = (): number => {
    const { estimatedRowSize, maxHeight } = this.props;

    const allRowsEstimatedHeight = estimatedRowSize * this.data.length;
    return allRowsEstimatedHeight > maxHeight ? maxHeight : allRowsEstimatedHeight;
  };

  handleResize = () =>
    this.setState({
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
    });

  recreateData = (): void => {
    const {
      content,
      forceHideHeaders,
      headers: { component: HeaderComponent, padding: headerPadding, firstHeaderPaddingOverride },
      items: { component: ItemComponent, onClick: itemOnClick, padding: itemPadding },
    } = this.props;

    this.data = [];

    content.forEach(({ headerContent, headerContent: { hidden: headerHidden }, itemsContent }, headerArrayIdx) => {
      if (!headerHidden && !forceHideHeaders) {
        this.data.push({
          ...headerContent,
          componentType: HeaderComponent,
          padding: headerArrayIdx === 0 ? firstHeaderPaddingOverride || headerPadding : headerPadding,
          selected: false,
        });
      }

      itemsContent.forEach(item => {
        if (!item.hidden) {
          this.data.push({
            ...item,
            componentType: ItemComponent,
            onItemClicked: itemOnClick,
            padding: itemPadding,
          });
        }
      });
    });
  };

  rowRenderer: React.FC<{ index: number; parent: any; style: object }> = ({ index, parent, style }) => {
    const { componentType: RowComponentType, id, insideNode, key, onItemClicked, padding, selected } = this.data[index];
    return (
      <CellMeasurer key={key} cache={this.cellMeasurerCache} columnIndex={0} parent={parent} rowIndex={index}>
        <RowComponentType
          key={key}
          onClick={onItemClicked ? () => onItemClicked({ id }) : undefined}
          padding={padding}
          selected={selected}
          style={style}
        >
          {insideNode}
        </RowComponentType>
      </CellMeasurer>
    );
  };

  render(): React.ReactNode {
    const { backgroundColor, estimatedRowSize, margin, noRowsRenderer, onAllAndNonzeroNodesHidden } = this.props;
    const { listWidth } = this.state;
    this.recreateData();

    return (
      <Wrapper backgroundColor={backgroundColor} margin={margin}>
        {this.areAllAndNonzeroNodesHidden() && onAllAndNonzeroNodesHidden}
        <div
          style={{
            display: this.areAllAndNonzeroNodesHidden() ? 'none' : undefined,
            flex: '1 1 auto',
            height: this.calculateListHeight(),
          }}
        >
          <AutoSizer disableWidth>
            {({ height }) => (
              <List
                ref={this.listRef}
                deferredMeasurementCache={this.cellMeasurerCache}
                estimatedRowSize={estimatedRowSize}
                height={height}
                noRowsRenderer={noRowsRenderer}
                rowCount={this.data.length}
                rowHeight={this.cellMeasurerCache.rowHeight || estimatedRowSize}
                rowRenderer={this.rowRenderer}
                width={listWidth}
              />
            )}
          </AutoSizer>
        </div>
      </Wrapper>
    );
  }
}
